import { Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { Laboratory } from 'src/app/_models/laboratory';
import {
  LAB_ADMIN_ROLE_LIST,
  OGI_ADMIN_ROLE_LIST,
  OGI_SUPER_ADMIN_ONLY,
  Role,
  SUPER_ADMIN_ROLE_LIST,
} from 'src/app/_models/role';
import {
  User,
  UserUpdate,
  UserStatus,
  UserStatusPrimaryDisplay,
} from 'src/app/_models/user';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { LaboratoryService } from 'src/app/_services/laboratory.service';
import { ModalService } from 'src/app/_services/modal.service';
import { formatLabel } from 'src/app/_helpers/format-role.pipe';
import { UserService } from 'src/app/_services/user.service';
import { UserUpdateResponse } from 'src/app/_models/api-responses';
import { FormatRolePipe } from '../../_helpers/format-role.pipe';
import { RoleCheckPipe } from '../../_helpers/role-check.pipe';
import { ValidationModalComponent } from '../../_components/modals/validation-modal/validation-modal.component';
import { ModalComponent } from '../../_components/modals/modal.component';
import { ErrorPageComponent } from '../../_components/error-page/error-page.component';
import { DropdownFormFieldComponent } from '../../_components/forms/dropdown-form-field/dropdown-form-field.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { LoadingSpinnerComponent } from '../../_components/loading-spinner/loading-spinner.component';
import { IconButtonComponent } from '../../_components/buttons/icon-button/icon-button.component';
import { UnderlinedDropdownFormFieldComponent } from '../../_components/forms/underlined-dropdown-form-field/underlined-dropdown-form-field.component';
import { ButtonComponent } from '../../_components/buttons/button/button.component';
import { UnderlinedFormFieldComponent } from '../../_components/forms/underlined-form-field/underlined-form-field.component';
import { SmallLoadingSpinnerComponent } from 'src/app/_components/small-loading-spinner/small-loading-spinner.component';
import { ToastComponent } from 'src/app/_components/toast/toast.component';
import { StatusIndicatorComponent } from 'src/app/_components/chips/status-indicator/status-indicator.component';
import { MatIconButtonComponent } from 'src/app/_components/buttons/mat-icon-button/mat-icon-button.component';
import { ButtonCardComponent } from 'src/app/_components/cards/button-card/button-card.component';

@Component({
  selector: 'app-lab-details',
  templateUrl: './lab-details.component.html',
  styleUrls: ['./lab-details.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    UnderlinedFormFieldComponent,
    ButtonComponent,
    UnderlinedDropdownFormFieldComponent,
    IconButtonComponent,
    LoadingSpinnerComponent,
    MatIcon,
    MatTooltip,
    DropdownFormFieldComponent,
    ErrorPageComponent,
    ModalComponent,
    ValidationModalComponent,
    RoleCheckPipe,
    FormatRolePipe,
    SmallLoadingSpinnerComponent,
    ToastComponent,
    StatusIndicatorComponent,
    MatIconButtonComponent,
    ButtonCardComponent,
  ],
})
export class LabDetailsComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly labService = inject(LaboratoryService);
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly laboratoryService = inject(LaboratoryService);

  private readonly unsubscribe$ = new Subject<void>();
  private successToast?: ReturnType<typeof setTimeout>;

  // Permissions
  laboratory: Laboratory;
  currentUser: User;
  users: User[] = [];
  availableUsers: User[] | null = null;
  addUserError: ErrorObject = null;
  selectedUserId: number;
  currentLabOrgId: number;
  updateUserId: number;
  UserStatusPrimaryDisplay = UserStatusPrimaryDisplay;

  // controls
  isUpdateLabProcessing = false;
  isUpdateRoleProcessing = false;
  isAddUserToLabProcessing = false;
  isLoading = true;
  labMembersTableLoading = false;
  labMemembersDropdownLoading = false;
  allowNavigation: Subject<boolean> = new Subject<boolean>();
  error: ErrorObject = null;
  updateLabSuccessMessage: string;
  isRemoveUserFromLabProcessing = false;
  removeUserFromLabId: number;
  deleteLabSuccessMessage: string;
  isProcessing = false;

  // Toast
  successResponse: string = '';
  displayToast = false;

  // Table control variables
  currentFilters = [];
  currentSort: {
    sortBy: string;
    sortDirection: string;
  } = {
    sortBy: 'LastName',
    sortDirection: 'asc',
  };

  // Pagination
  pageRequested = 1;
  pageSize = 5;
  totalCount = 0;
  totalPages = 0;
  currentPage = 1;
  hasNext = false;
  hasPrevious = false;
  selectedRole: Role = null;

  // make role enum available in the template
  Role = Role;
  readonly labAdminRoles = LAB_ADMIN_ROLE_LIST;
  readonly superAdminRoles = SUPER_ADMIN_ROLE_LIST;
  readonly ogiSuperAdminOnly = OGI_SUPER_ADMIN_ONLY;
  UserStatus = UserStatus;

  labNameField = new FormBase({
    key: 'labName',
    label: 'Lab Name',
    type: 'text',
    placeholder: 'Lab Name',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  editLabForm = this.formBuilder.group({
    [this.labNameField.key]: ['', Validators.required],
  });

  addUserField = new FormBase({
    key: 'addUser',
    label: 'Add user to laboratory',
    type: 'select',
    placeholder: 'Select user',
    disabled: false,
    required: false,
    value: '',
    options: [],
  });

  addUserForm = this.formBuilder.group({
    [this.addUserField.key]: [],
  });

  userRoleField = new FormBase({
    key: 'role',
    label: 'Role',
    type: 'select',
    placeholder: 'Select role',
    disabled: false,
    required: false,
    value: '',
    options: [
      { key: Role.Read_Access_User, value: 'Read Access User' },
      { key: Role.Run_Access_User, value: 'Run Access User' },
      { key: Role.Edit_Access_User, value: 'Edit Access User' },
      { key: Role.Lab_Administrator, value: 'Lab Administrator' },
    ],
  });

  editUserForm = this.formBuilder.group({
    [this.userRoleField.key]: [''],
  });

  usersPerPageField = new FormBase({
    key: 'usersPerPage',
    label: 'Page Size',
    type: 'dropdown',
    placeholder: '',
    disabled: false,
    required: false,
    value: '5', // Set default value to 5
    options: [
      { key: '5', value: '5' },
      { key: '10', value: '10' },
      { key: '25', value: '25' },
      { key: '50', value: '50' },
      { key: '100', value: '100' },
    ],
  });

  userRolesFilter = new FormBase({
    key: 'userRoleFilter',
    label: 'Select role to filter by:',
    type: 'dropdown',
    placeholder: 'Filter by role',
    disabled: false,
    required: false,
    value: null,
    options: [
      { key: null, value: 'All' },
      { key: 1, value: 'Ogi Super Administrator' },
      { key: 2, value: 'Ogi Laboratory Administrator' },
      { key: 7, value: 'Super User' },
      { key: 3, value: 'Lab Administrator' },
      { key: 4, value: 'Edit Access User' },
      { key: 6, value: 'Read Access User' },
      { key: 5, value: 'Run Access User' },
    ],
  });

  usersTableControls = this.formBuilder.group({
    [this.usersPerPageField.key]: new FormControl(this.usersPerPageField.value),
    [this.userRolesFilter.key]: new FormControl(this.userRolesFilter.value),
  });

  ngOnInit(): void {
    this.currentUser = this.authenticationService.currentUserValue;
    // Now set the available roles based on the current user's role
    this.setAvailableUserRoles(this.currentUser.role);
    this.authenticationService.selectedLab$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((lab) => {
        this.laboratory = lab;

        if (
          !LAB_ADMIN_ROLE_LIST.includes(this.currentUser.role) ||
          (this.currentUser.role === Role.Super_User &&
            !this.laboratory?.isOwnLab)
        ) {
          this.editLabForm.controls['labName'].disable();
        } else {
          this.editLabForm.controls['labName'].enable();
        }
      });

    // Check for saved sort, filter, and pagination settings
    if (this.labService.paginationCache !== null) {
      this.pageSize = this.labService.paginationCache.pageSize;
      this.pageRequested = this.labService.paginationCache.pageNumber;
      this.usersTableControls.patchValue({
        [this.usersPerPageField.key]: this.pageSize.toString(),
      });
    }

    // Subscribe to changes in the users per page dropdown
    this.usersTableControls
      .get(this.usersPerPageField.key)
      .valueChanges.subscribe((numOfUsers) => {
        this.pageSize = numOfUsers as number;
        this.pageRequested = 1;
        this.getLabMembers();
      });

    // Subscribe to the value changes of the request status dropdown
    this.usersTableControls
      .get(this.userRolesFilter.key)
      .valueChanges.subscribe((status) => {
        this.selectedRole = status as Role;
        this.getLabMembers();
      });

    this.addUserForm
      .get(this.addUserField.key)
      .valueChanges.subscribe((value: { key: string; value: string }) => {
        const selectedUser = this.addUserField.options.find((option) => {
          return option.key === value.key;
        });
        this.selectedUserId = selectedUser?.key as number;
      });

    if (this.laboratory) {
      this.initialiseForm();
      this.getLab();
    } else {
      this.isLoading = false;
    }
  }

  initialiseForm(): void {
    this.editLabForm.setValue({
      labName: this.laboratory.laboratoryName,
    });

    if (!LAB_ADMIN_ROLE_LIST.includes(this.currentUser.role)) {
      this.editLabForm.disable();
    }
    this.editLabForm.markAsPristine();
  }

  getLab(): void {
    this.isLoading = true;

    this.laboratoryService.getLab(this.laboratory.laboratoryId).subscribe({
      next: (response: Laboratory) => {
        this.currentLabOrgId = response.organisationId;
        this.isLoading = false;
        this.getLabMembers();
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  getAvailableUsersForLab(): void {
    this.labMemembersDropdownLoading = true;
    const orgId =
      this.currentLabOrgId.toString() !==
      this.currentUser.organisationId.toString()
        ? this.currentLabOrgId
        : null;

    // Only pass in org id if ogi admin is viewing lab in different org
    this.labService
      .getAvailableUsersForLab(this.laboratory.laboratoryId, orgId)
      .subscribe({
        next: (response) => {
          this.addUserField.options = response.map((user) => {
            return {
              key: user.id,
              value:
                user.firstName +
                ' ' +
                user.lastName +
                ' - ' +
                formatLabel(user.role),
            };
          });
          this.availableUsers = response;
          this.labMemembersDropdownLoading = false;
        },
        error: (error: ErrorObject) => {
          this.error = error;
          this.labMemembersDropdownLoading = false;
        },
      });
  }

  setAvailableUserRoles(role: Role): void {
    if (OGI_ADMIN_ROLE_LIST.includes(role)) {
      this.userRoleField.options.push({
        key: Role.OGI_Laboratory_Administrator,
        value: 'OGI Laboratory Administrator',
      });
    }

    if (role === Role.OGI_Super_Administrator) {
      this.userRoleField.options.push({
        key: Role.OGI_Super_Administrator,
        value: 'OGI Super Administrator',
      });
    }
  }

  addUserToLab(): void {
    if (this.isAddUserToLabProcessing || this.addUserForm.invalid) {
      return;
    }

    this.isAddUserToLabProcessing = true;
    this.error = null;
    this.updateLabSuccessMessage = '';

    this.labService
      .addUserToLab(this.selectedUserId, this.laboratory.laboratoryId)
      .subscribe({
        next: (response: string) => {
          // call to update user table/get lab members for dropdown
          this.getLabMembers();
          if (
            this.currentUser != null &&
            this.currentUser.id.toString() === this.selectedUserId.toString() &&
            this.currentUser.role === Role.Super_User
          ) {
            this.laboratory.isOwnLab = true;
            // Build a new laboratories array rather than pushing onto the
            // existing one — currentUser is the same object reference held
            // by AuthenticationService, so mutating it in place would leave
            // currentUser$'s distinctUntilChanged comparing that object
            // against itself, silently suppressing the emission.
            this.currentUser = {
              ...this.currentUser,
              laboratories: [
                ...(this.currentUser.laboratories ?? []),
                this.laboratory,
              ],
            };
            this.authenticationService.updateCurrentUser(this.currentUser);
            this.authenticationService.setSelectedLaboratory(this.laboratory);
          }
          this.isAddUserToLabProcessing = false;
          this.selectedUserId = null;
          this.addUserForm.reset();
          this.successResponse = response;
          this.showToast();
        },
        error: (error: ErrorObject) => {
          this.isAddUserToLabProcessing = false;
          this.error = error;
          this.openModal('error');
        },
      });
  }

  getLabMembers(): void {
    if (this.labMembersTableLoading) {
      return;
    }

    this.labMembersTableLoading = true;

    this.labService
      .getLabMembers(
        this.laboratory.laboratoryId,
        false,
        this.currentSort.sortBy,
        this.currentSort.sortDirection,
        this.pageRequested,
        this.pageSize,
        this.selectedRole,
      )
      .subscribe({
        next: (response) => {
          const { users, paginationData } = response;
          const { CurrentPage, PageSize, TotalPages, HasNext, HasPrevious } =
            paginationData;

          this.users = users;
          this.currentPage = CurrentPage;
          this.pageSize = PageSize;
          this.totalPages = TotalPages;
          this.hasNext = HasNext;
          this.hasPrevious = HasPrevious;

          this.labService.onUpdatePageNumber(this.currentPage);
          this.labService.onUpdatePageSize(this.pageSize);

          this.labMembersTableLoading = false;
          if (LAB_ADMIN_ROLE_LIST.includes(this.currentUser.role)) {
            this.getAvailableUsersForLab();
          }
        },
        error: (error: ErrorObject) => {
          this.error = error;
          this.labMembersTableLoading = false;
        },
      });
  }

  canEditUser(user: User): boolean {
    return (
      this.superAdminRoles.includes(this.currentUser.role) &&
      user.userStatus === UserStatus.Active
    );
  }

  hideUserRoleDropdown(user: User): boolean {
    if (
      !LAB_ADMIN_ROLE_LIST.includes(this.currentUser.role) ||
      // Ogi lab admins, lab admins and super users cannot change roles of ogi super admins
      ((this.currentUser.role === Role.OGI_Laboratory_Administrator ||
        this.currentUser.role === Role.Lab_Administrator ||
        this.currentUser.role === Role.Super_User) &&
        user.role === Role.OGI_Super_Administrator) ||
      // Ogi lab admins and lab admins cannot change roles of super users
      ((this.currentUser.role === Role.OGI_Laboratory_Administrator ||
        this.currentUser.role === Role.Lab_Administrator) &&
        user.role === Role.Super_User) ||
      this.currentUser.id.toString() === user.id.toString()
    ) {
      return true;
    } else return false;
  }

  onSort(sortTerm: string): void {
    if (this.currentSort.sortBy === sortTerm) {
      if (this.currentSort.sortDirection === 'asc') {
        this.currentSort.sortDirection = 'desc';
      } else {
        this.currentSort.sortDirection = 'asc';
      }
    } else {
      this.currentSort.sortBy = sortTerm;
      this.currentSort.sortDirection = 'asc';
    }
    this.getLabMembers();
  }

  getClasses(sortTerm: string): string[] {
    const active = this.currentSort.sortBy === sortTerm ? 'active' : '';
    return [active];
  }

  onAddNewUser(): void {
    void this.router.navigate(['user/add-user']);
  }

  showToast() {
    this.displayToast = true;
    this.successToast = setTimeout(() => {
      this.displayToast = false;
    }, 5000);
  }

  getPrevious(): void {
    // Reset the role dropdown when navigating to the next page to stop role dropdown being prepopulated
    // with the last selected role
    this.editUserForm.reset();
    this.pageRequested = this.currentPage - 1;
    this.getLabMembers();
  }

  getNext(): void {
    // Reset the role dropdown when navigating to the next page to stop role dropdown being prepopulated
    // with the last selected role
    this.editUserForm.reset();
    this.pageRequested = this.currentPage + 1;
    this.getLabMembers();
  }

  onSubmit(): void {
    if (this.editLabForm.invalid || this.isUpdateLabProcessing) {
      return;
    }

    this.isUpdateLabProcessing = true;
    this.error = null;
    this.updateLabSuccessMessage = '';

    // update lab
    this.labService
      .updateLab(this.laboratory.laboratoryId, this.editLabForm.value.labName)
      .subscribe({
        next: (response: string) => {
          this.isUpdateLabProcessing = false;
          this.laboratory.laboratoryName = this.editLabForm.value.labName;
          this.authenticationService.setSelectedLaboratory(this.laboratory);
          this.successResponse = response;
          this.showToast();
        },
        error: (error: ErrorObject) => {
          this.isUpdateLabProcessing = false;
          this.error = error;
          this.openModal('error');
        },
      });
  }

  get f(): FormGroup['controls'] {
    return this.editUserForm.controls;
  }

  onUpdateUser(userId: number): void {
    if (this.editUserForm.invalid || this.isUpdateRoleProcessing) {
      return;
    }

    this.isUpdateRoleProcessing = true;
    this.updateUserId = userId;
    this.error = null;
    this.updateLabSuccessMessage = '';

    const userUpdate: UserUpdate = {
      id: userId,
      firstName: null,
      lastName: null,
      userRole: this.f.role.value as Role,
    };

    this.userService
      .updateUser(userUpdate)
      .pipe()
      .subscribe({
        next: (data: UserUpdateResponse) => {
          this.isUpdateRoleProcessing = false;
          this.updateUserId = null;
          this.getLabMembers();
          this.successResponse = data.message;
          this.showToast();
        },
        error: (error: ErrorObject) => {
          this.isUpdateRoleProcessing = false;
          this.updateUserId = null;
          this.error = error;
          this.openModal('error');
        },
      });
  }

  onEditUser(userId: number): void {
    if (this.currentUser.id.toString() === userId.toString()) {
      void this.router.navigate(['account/user-profile']);
    } else {
      void this.router.navigate([`user/update-user/${userId}`]);
    }
  }

  handleRemoveUserFromLab(userId: number): void {
    this.removeUserFromLabId = userId;
    this.openModal('remove-user');
  }

  onRemoveUserFromLab(): void {
    if (this.isRemoveUserFromLabProcessing) {
      return;
    }

    this.error = null;
    this.isRemoveUserFromLabProcessing = true;

    this.labService
      .removeUserFromLab(this.removeUserFromLabId, this.laboratory.laboratoryId)
      .subscribe({
        next: (response: string) => {
          this.isRemoveUserFromLabProcessing = false;
          this.removeUserFromLabId = null;
          this.closeModal('remove-user');
          this.successResponse = response;
          this.showToast();
          this.getLabMembers();
        },
        error: (error: ErrorObject) => {
          this.error = error;
          this.isRemoveUserFromLabProcessing = false;
          this.removeUserFromLabId = null;
        },
      });
  }

  deleteLab(): void {
    this.isProcessing = true;
    this.error = null;

    this.labService.deleteLab(this.laboratory.laboratoryId).subscribe({
      next: (response: string) => {
        this.isProcessing = false;
        this.closeModal('delete-lab');
        this.successResponse = response;
        this.showToast();
      },
      error: (error: ErrorObject) => {
        this.error = error;
        this.openModal('error');
      },
    });
  }

  openModal(modalId: string): void {
    this.modalService.open(modalId);
  }

  closeModal(modalId: string): void {
    this.modalService.close(modalId);
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'close-button':
        this.closeModal('delete-lab');
        this.error = null;
        break;
      case 'cancel-button':
        this.allowNavigation.next(false);
        this.closeModal('attention');
        break;
      case 'confirm-navigation':
        this.allowNavigation.next(true);
        this.closeModal('attention');
        break;
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (this.successToast) {
      clearTimeout(this.successToast);
    }
  }
}
